import React from 'react';
import CountryFlag from './CountryFlag';
import PlayerCell from './PlayerCell';
import './RankingTable.css';

const QUALIFICATION_CUTOFF = 60;

const trendSymbol = (direction) => {
  if (direction === 'up') return '⇑';
  if (direction === 'down') return '⇓';
  return '';
};

const entryKey = (entry) =>
  `${entry?.firstName || ''} ${entry?.lastName || ''}|${entry?.country || ''}`
    .trim()
    .toLowerCase();

const previousRankForEntry = (entry, rankHistory = {}) => {
  const history = rankHistory[entryKey(entry)];
  if (!history || history.length < 2) return null;
  return history[history.length - 2]?.olympicRank || null;
};

const trendDetails = (entry, previousRank) => {
  if (!previousRank) {
    return {
      direction: entry.changeDirection,
      amount: null,
    };
  }

  const delta = previousRank - entry.olympicRank;
  if (delta > 0) return { direction: 'up', amount: delta };
  if (delta < 0) return { direction: 'down', amount: Math.abs(delta) };
  return { direction: 'same', amount: 0 };
};

const playerName = (entry) =>
  `${entry?.firstName || ''} ${entry?.lastName || ''}`.trim() || 'Unnamed athlete';

const formatPoints = (points) =>
  typeof points === 'number' && Number.isFinite(points) ? points.toFixed(2) : '—';

const RankingTable = ({ entries, loading, onRowClick, rankHistory = {}, gender = 'men' }) => {
  const worldRankLabel = gender === 'women' ? 'Rolex Rank' : 'OWGR';

  if (loading && !entries?.length) {
    return (
      <div className="pub-table-loading">
        <div className="spinner-border text-success" role="status" />
      </div>
    );
  }

  if (!entries?.length) {
    return <p className="pub-table-empty">No athletes match your search.</p>;
  }

  const columnCount = 7;
  const handleRowKeyDown = (event, entry) => {
    if (!onRowClick || !['Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    onRowClick(entry);
  };

  return (
    <div className="pub-table-wrap">
      <table className="pub-table">
        <colgroup>
          <col className="col-rank" />
          <col className="col-last-week" />
          <col className="col-trend" />
          <col className="col-player" />
          <col className="col-wr" />
          <col className="col-points" />
          <col className="col-country" />
        </colgroup>
        <thead>
          <tr>
            <th className="col-rank">This Week</th>
            <th className="col-last-week">Last Week</th>
            <th className="col-trend">Trend</th>
            <th className="col-player">Athlete</th>
            <th className="col-wr">{worldRankLabel}</th>
            <th className="col-points">Points</th>
            <th className="col-country">Country</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, idx) => {
            const prevEntry = idx > 0 ? entries[idx - 1] : null;
            const showCutoff =
              entry.olympicRank > QUALIFICATION_CUTOFF &&
              (!prevEntry || prevEntry.olympicRank <= QUALIFICATION_CUTOFF);
            const isBelowCutoff = entry.olympicRank > QUALIFICATION_CUTOFF;
            const previousRank = previousRankForEntry(entry, rankHistory);
            const trend = trendDetails(entry, previousRank);
            const hasMovement = ['up', 'down'].includes(trend.direction);

            return (
              <React.Fragment key={entry._id}>
                {showCutoff && (
                  <tr className="pub-cutoff-row" aria-label="Olympic qualification cutoff">
                    <td colSpan={columnCount}>
                      <div className="pub-cutoff-banner">
                        <span className="pub-cutoff-line" />
                        <span className="pub-cutoff-label">
                          Olympic Qualification Cutoff - Top {QUALIFICATION_CUTOFF}
                        </span>
                        <span className="pub-cutoff-line" />
                      </div>
                    </td>
                  </tr>
                )}
                <tr
                  className={`pub-row ${onRowClick ? 'pub-row--clickable' : ''} ${
                    isBelowCutoff ? 'pub-row--below-cutoff' : ''
                  } ${entry.olympicRank === QUALIFICATION_CUTOFF ? 'pub-row--cutoff-last' : ''} ${
                    entry.olympicRank === QUALIFICATION_CUTOFF + 1 ? 'pub-row--first-out' : ''
                  }`}
                  onClick={() => onRowClick?.(entry)}
                  onKeyDown={(event) => handleRowKeyDown(event, entry)}
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? 'button' : undefined}
                  aria-label={onRowClick ? `View ${playerName(entry)} progression` : undefined}
                >
                  <td className="col-rank">
                    <span className="rank-num rank-num--current">{entry.olympicRank}</span>
                  </td>
                  <td className="col-last-week">
                    <span className="rank-num rank-num--previous">
                      {previousRank || '—'}
                    </span>
                  </td>
                  <td className="col-trend">
                    <span className="mobile-rank-stack">
                      {hasMovement ? (
                        <span className={`trend-indicator trend-${trend.direction}`}>
                          {trendSymbol(trend.direction)}
                          {trend.amount ? <span>{trend.amount}</span> : null}
                        </span>
                      ) : (
                        <span className="trend-blank">—</span>
                      )}
                      <span className="mobile-rank-meta">
                        <span>
                          {worldRankLabel} {entry.worldRankPosition ? `#${entry.worldRankPosition}` : '—'}
                        </span>
                        <span>LW {previousRank || '—'}</span>
                      </span>
                    </span>
                  </td>
                  <td className="col-player">
                    <PlayerCell
                      firstName={entry.firstName}
                      lastName={entry.lastName}
                      country={entry.country}
                      headshotUrl={entry.headshotUrl}
                    />
                    <div className="player-mobile-meta" aria-hidden>
                      <span className="player-mobile-country">
                        <CountryFlag code={entry.country} size={16} />
                        {entry.country}
                      </span>
                    </div>
                  </td>
                  <td className="col-wr" data-label={worldRankLabel}>
                    {entry.worldRankPosition ? `#${entry.worldRankPosition}` : '—'}
                  </td>
                  <td className="col-points" data-label="Points">
                    {formatPoints(entry.worldRankPoints)}
                  </td>
                  <td className="col-country">
                    <span className="country-cell">
                      <CountryFlag code={entry.country} size={22} />
                      <span className="country-code">{entry.country}</span>
                    </span>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RankingTable;
